import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';
import CourseManagement from '../components/CourseManagement';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  status: string;
  created_at: string;
  last_login_at: string | null;
  organization_id?: string;
  organization_name?: string;
  location_id?: string;
  location_name?: string;
}

interface Location {
  id: string;
  name: string;
  organization_id: string;
  organization_name: string;
  city?: string;
  state?: string;
  user_count: number;
}

interface Organization {
  id: string;
  name: string;
  tier: string;
  status: string;
  user_count: number;
  location_count: number;
}

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const [users, setUsers] = useState<User[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [showCreateLocationModal, setShowCreateLocationModal] = useState(false);
  const [newOrg, setNewOrg] = useState({ name: '', tier: 'basic' });
  const [newLocation, setNewLocation] = useState({ name: '', organizationId: '', address: '', city: '', state: '', zipCode: '', phone: '' });
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    role: 'user',
    organizationId: '',
    locationId: ''
  });
  const [newRole, setNewRole] = useState('');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [selectedOrganization, setSelectedOrganization] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'users' | 'organizations' | 'locations' | 'courses'>('users');

  const isSuperAdmin = currentUser?.role === 'super_admin';
  const isOrgAdmin = currentUser?.role === 'org_admin';
  const isLocationAdmin = currentUser?.role === 'location_admin';
  const isAdmin = isSuperAdmin || isOrgAdmin || isLocationAdmin;

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [currentUser, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch users (filtered by role)
      const usersResponse = await api.get('/roles/users');
      setUsers(usersResponse.data);

      // Fetch locations (filtered by role)
      const locationsResponse = await api.get('/locations');
      setLocations(locationsResponse.data);

      // Fetch organizations (super admin only)
      if (currentUser?.role === 'super_admin') {
        const orgsResponse = await api.get('/roles/organizations');
        setOrganizations(orgsResponse.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setMessage({ type: 'error', text: 'Failed to load data' });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || !newPassword) return;

    try {
      await api.post('/password/admin-reset', {
        userId: selectedUser.id,
        newPassword
      });

      setMessage({ 
        type: 'success', 
        text: `Password reset successfully for ${selectedUser.email}` 
      });
      setShowResetModal(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to reset password';
      const details = error.response?.data?.details;
      setMessage({ 
        type: 'error', 
        text: details ? `${errorMessage}: ${details.join(', ')}` : errorMessage 
      });
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedUser || !newRole) return;

    try {
      await api.post('/roles/update', {
        userId: selectedUser.id,
        newRole
      });

      setMessage({ 
        type: 'success', 
        text: `Role updated successfully for ${selectedUser.email}` 
      });
      setShowRoleModal(false);
      setSelectedUser(null);
      setNewRole('');
      fetchData(); // Refresh user list
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to update role';
      setMessage({ 
        type: 'error', 
        text: errorMessage 
      });
    }
  };

  const openResetModal = (user: User) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowResetModal(true);
    setMessage(null);
  };

  const openRoleModal = (user: User) => {
    setSelectedUser(user);
    setNewRole(user.role);
    setShowRoleModal(true);
    setMessage(null);
  };

  const openCreateModal = () => {
    setNewUser({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      role: 'user',
      organizationId: isSuperAdmin ? '' : (currentUser?.tenantId || ''),
      locationId: isLocationAdmin ? (currentUser?.locationId || '') : ''
    });
    setShowCreateModal(true);
    setMessage(null);
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password || !newUser.firstName || !newUser.lastName) {
      setMessage({ type: 'error', text: 'All fields are required' });
      return;
    }

    try {
      await api.post('/auth/register', {
        email: newUser.email,
        password: newUser.password,
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        tenantId: newUser.organizationId,
        locationId: newUser.locationId || null,
        role: newUser.role
      });

      setMessage({ 
        type: 'success', 
        text: `User ${newUser.email} created successfully` 
      });
      setShowCreateModal(false);
      fetchData(); // Refresh user list
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to create user';
      const details = error.response?.data?.details;
      setMessage({ 
        type: 'error', 
        text: details ? `${errorMessage}: ${details.join(', ')}` : errorMessage 
      });
    }
  };

  const handleCreateOrganization = async () => {
    if (!newOrg.name) {
      setMessage({ type: 'error', text: 'Organization name is required' });
      return;
    }

    try {
      const response = await api.post('/admin/organizations', {
        name: newOrg.name,
        tier: newOrg.tier
      });

      setMessage({ 
        type: 'success', 
        text: `Organization "${newOrg.name}" created successfully` 
      });
      setShowCreateOrgModal(false);
      setNewOrg({ name: '', tier: 'basic' });
      fetchData(); // Refresh data
      
      // If creating from user modal, set the new org as selected
      if (response.data.tenant?.id) {
        setNewUser({ ...newUser, organizationId: response.data.tenant.id });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to create organization';
      setMessage({ type: 'error', text: errorMessage });
    }
  };

  const handleCreateLocation = async () => {
    if (!newLocation.name || !newLocation.organizationId) {
      setMessage({ type: 'error', text: 'Location name and organization are required' });
      return;
    }

    try {
      const response = await api.post('/locations', {
        organizationId: newLocation.organizationId,
        name: newLocation.name,
        address: newLocation.address,
        city: newLocation.city,
        state: newLocation.state,
        zipCode: newLocation.zipCode,
        phone: newLocation.phone
      });

      setMessage({ 
        type: 'success', 
        text: `Location "${newLocation.name}" created successfully` 
      });
      setShowCreateLocationModal(false);
      setNewLocation({ name: '', organizationId: '', address: '', city: '', state: '', zipCode: '', phone: '' });
      fetchData(); // Refresh data
      
      // If creating from user modal, set the new location as selected
      if (response.data?.id) {
        setNewUser({ ...newUser, locationId: response.data.id });
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Failed to create location';
      setMessage({ type: 'error', text: errorMessage });
    }
  };

  const validatePasswordStrength = (password: string) => {
    return {
      length: password.length >= 8,
      letters: /[a-zA-Z]/.test(password),
      numbers: /\d/.test(password),
      special: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)
    };
  };

  const passwordChecks = newPassword ? validatePasswordStrength(newPassword) : null;
  const isPasswordValid = passwordChecks && 
    passwordChecks.length && 
    passwordChecks.letters && 
    passwordChecks.numbers && 
    passwordChecks.special;

  // Get unique organizations from users (fallback for non-super admins)
  const userOrganizations = Array.from(new Set(users.map(u => u.organization_id)))
    .map(orgId => {
      const user = users.find(u => u.organization_id === orgId);
      return {
        id: orgId,
        name: user?.organization_name || 'Unknown'
      };
    })
    .filter(org => org.id);

  // Use fetched organizations for super admin, otherwise derive from users
  // Deduplicate by ID to prevent duplicates
  const allOrgs = isSuperAdmin && organizations.length > 0 ? organizations : userOrganizations;
  const displayOrganizations = Array.from(
    new Map(allOrgs.map(org => [org.id, org])).values()
  );

  // Filter users by selected organization and location
  const filteredUsers = users.filter(u => {
    if (selectedOrganization !== 'all' && u.organization_id !== selectedOrganization) return false;
    if (selectedLocation !== 'all' && u.location_id !== selectedLocation) return false;
    return true;
  });

  // Filter locations by selected organization
  const filteredLocations = selectedOrganization === 'all' 
    ? locations 
    : locations.filter(l => l.organization_id === selectedOrganization);

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'super_admin': return 'bg-purple-500/20 text-purple-300';
      case 'org_admin': return 'bg-blue-500/20 text-blue-300';
      case 'location_admin': return 'bg-cyan-500/20 text-cyan-300';
      default: return 'bg-slate-500/20 text-slate-300';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'org_admin': return 'Org Admin';
      case 'location_admin': return 'Location Admin';
      default: return 'User';
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="border-b border-primary-500/20 bg-slate-900/95 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-slate-400 hover:text-white transition-colors"
            >
              ← Back to Dashboard
            </button>
            <div className="text-sm">
              <span className="text-slate-400">Logged in as: </span>
              <span className="font-semibold">{currentUser?.email}</span>
              <span className={`ml-2 px-2 py-1 rounded text-xs ${getRoleBadgeColor(currentUser?.role || '')}`}>
                {getRoleLabel(currentUser?.role || '')}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">
            {isSuperAdmin && '👑 Super Admin Panel'}
            {isOrgAdmin && '🏢 Organization Admin Panel'}
            {isLocationAdmin && '📍 Location Admin Panel'}
          </h1>
          <p className="text-slate-400">
            {isSuperAdmin && 'Manage users across all organizations and locations'}
            {isOrgAdmin && 'Manage all locations and users in your organization'}
            {isLocationAdmin && 'Manage users in your location'}
          </p>
        </div>

        {message && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-500/20 border border-green-500/30 text-green-300' 
              : 'bg-red-500/20 border border-red-500/30 text-red-300'
          }`}>
            {message.text}
          </div>
        )}

        {/* Tab Navigation */}
        <div className="border-b border-slate-700 mb-6">
          <nav className="flex gap-4">
            <button
              onClick={() => setActiveTab('users')}
              className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'users'
                  ? 'border-primary-500 text-primary-400'
                  : 'border-transparent text-slate-400 hover:text-slate-300'
              }`}
            >
              👥 Users
            </button>
            {isSuperAdmin && (
              <>
                <button
                  onClick={() => setActiveTab('organizations')}
                  className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                    activeTab === 'organizations'
                      ? 'border-primary-500 text-primary-400'
                      : 'border-transparent text-slate-400 hover:text-slate-300'
                  }`}
                >
                  🏢 Organizations
                </button>
                <button
                  onClick={() => setActiveTab('locations')}
                  className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                    activeTab === 'locations'
                      ? 'border-primary-500 text-primary-400'
                      : 'border-transparent text-slate-400 hover:text-slate-300'
                  }`}
                >
                  📍 Locations
                </button>
              </>
            )}
            <button
              onClick={() => setActiveTab('courses')}
              className={`px-4 py-3 font-medium transition-colors border-b-2 ${
                activeTab === 'courses'
                  ? 'border-primary-500 text-primary-400'
                  : 'border-transparent text-slate-400 hover:text-slate-300'
              }`}
            >
              🎓 Courses
            </button>
          </nav>
        </div>

        {/* Users Tab */}
        {activeTab === 'users' && (
          <>
            {/* Filters (Super Admin and Org Admin) */}
            {(isSuperAdmin || isOrgAdmin) && (
          <div className="card mb-6">
            <div className="flex flex-wrap items-center gap-4">
              {/* Organization Filter (Super Admin Only) */}
              {isSuperAdmin && displayOrganizations.length > 0 && (
                <div className="flex items-center gap-2">
                  <label className="text-sm font-semibold">Organization:</label>
                  <select
                    value={selectedOrganization}
                    onChange={(e) => {
                      setSelectedOrganization(e.target.value);
                      setSelectedLocation('all'); // Reset location filter
                    }}
                    className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary-500"
                  >
                    <option value="all">All Organizations ({users.length} users)</option>
                    {displayOrganizations.map(org => (
                      <option key={org.id} value={org.id}>
                        {org.name} ({users.filter(u => u.organization_id === org.id).length} users)
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Location Filter */}
              {filteredLocations.length > 0 && (
                <div className="flex items-center gap-2">
                  <label className="text-sm font-semibold">Location:</label>
                  <select
                    value={selectedLocation}
                    onChange={(e) => setSelectedLocation(e.target.value)}
                    className="px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary-500"
                  >
                    <option value="all">All Locations ({filteredUsers.length} users)</option>
                    {filteredLocations.map(loc => (
                      <option key={loc.id} value={loc.id}>
                        {loc.name} ({users.filter(u => u.location_id === loc.id).length} users)
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Users Table */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold">User Management</h2>
            <button
              onClick={openCreateModal}
              className="btn btn-primary text-sm"
            >
              + Create User
            </button>
          </div>
          
          {loading ? (
            <div className="text-center py-8 text-slate-400">Loading users...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-sm font-semibold">Name</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold">Email</th>
                    {isSuperAdmin && <th className="text-left py-3 px-4 text-sm font-semibold">Organization</th>}
                    {(isSuperAdmin || isOrgAdmin) && <th className="text-left py-3 px-4 text-sm font-semibold">Location</th>}
                    <th className="text-left py-3 px-4 text-sm font-semibold">Role</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold">Status</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold">Last Login</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="border-b border-slate-800 hover:bg-slate-800/50">
                      <td className="py-3 px-4 text-sm">
                        {user.first_name} {user.last_name}
                      </td>
                      <td className="py-3 px-4 text-sm">{user.email}</td>
                      {isSuperAdmin && (
                        <td className="py-3 px-4 text-sm text-slate-400">
                          {user.organization_name || 'Unknown'}
                        </td>
                      )}
                      {(isSuperAdmin || isOrgAdmin) && (
                        <td className="py-3 px-4 text-sm text-slate-400">
                          {user.location_name || 'Not assigned'}
                        </td>
                      )}
                      <td className="py-3 px-4 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs ${getRoleBadgeColor(user.role)}`}>
                          {getRoleLabel(user.role)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          user.status === 'active' 
                            ? 'bg-green-500/20 text-green-300' 
                            : 'bg-red-500/20 text-red-300'
                        }`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-400">
                        {user.last_login_at 
                          ? new Date(user.last_login_at).toLocaleDateString() 
                          : 'Never'}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openResetModal(user)}
                            className="text-primary-400 hover:text-primary-300 text-sm"
                          >
                            Reset Password
                          </button>
                          {/* Show Change Role button based on permissions */}
                          {user.id !== currentUser?.id && (
                            <>
                              {/* Super admin can change any role except their own */}
                              {isSuperAdmin && (
                                <button
                                  onClick={() => openRoleModal(user)}
                                  className="text-purple-400 hover:text-purple-300 text-sm"
                                >
                                  Change Role
                                </button>
                              )}
                              {/* Org admin can change roles except super_admin and org_admin */}
                              {isOrgAdmin && user.role !== 'super_admin' && user.role !== 'org_admin' && (
                                <button
                                  onClick={() => openRoleModal(user)}
                                  className="text-purple-400 hover:text-purple-300 text-sm"
                                >
                                  Change Role
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
          </>
        )}

        {/* Courses Tab */}
        {activeTab === 'courses' && (
          <CourseManagement />
        )}

        {/* Organizations Tab - TODO */}
        {activeTab === 'organizations' && isSuperAdmin && (
          <div className="card">
            <h2 className="text-xl font-bold mb-4">Organizations</h2>
            <p className="text-slate-400">Organization management coming soon...</p>
          </div>
        )}

        {/* Locations Tab - TODO */}
        {activeTab === 'locations' && isSuperAdmin && (
          <div className="card">
            <h2 className="text-xl font-bold mb-4">Locations</h2>
            <p className="text-slate-400">Location management coming soon...</p>
          </div>
        )}
      </main>

      {/* Reset Password Modal */}
      {showResetModal && selectedUser && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-900 border border-slate-700 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Reset Password</h3>
            
            <div className="mb-4 p-3 bg-slate-800 rounded-lg">
              <div className="text-sm text-slate-400">Resetting password for:</div>
              <div className="font-semibold">{selectedUser.first_name} {selectedUser.last_name}</div>
              <div className="text-sm text-slate-400">{selectedUser.email}</div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary-500"
                placeholder="Enter new password"
              />
            </div>

            {/* Password Requirements */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 mb-4">
              <div className="text-sm font-semibold mb-2">Password Requirements:</div>
              <ul className="space-y-1 text-sm">
                <li className={passwordChecks?.length ? 'text-green-400' : 'text-slate-400'}>
                  {passwordChecks?.length ? '✓' : '○'} At least 8 characters long
                </li>
                <li className={passwordChecks?.letters ? 'text-green-400' : 'text-slate-400'}>
                  {passwordChecks?.letters ? '✓' : '○'} Contains letters (a-z, A-Z)
                </li>
                <li className={passwordChecks?.numbers ? 'text-green-400' : 'text-slate-400'}>
                  {passwordChecks?.numbers ? '✓' : '○'} Contains numbers (0-9)
                </li>
                <li className={passwordChecks?.special ? 'text-green-400' : 'text-slate-400'}>
                  {passwordChecks?.special ? '✓' : '○'} Contains special characters (!@#$%^&* etc.)
                </li>
              </ul>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowResetModal(false);
                  setSelectedUser(null);
                  setNewPassword('');
                }}
                className="flex-1 btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleResetPassword}
                disabled={!isPasswordValid}
                className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Role Modal */}
      {showRoleModal && selectedUser && (isSuperAdmin || isOrgAdmin) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-900 border border-slate-700 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold mb-4">Change User Role</h3>
            
            <div className="mb-4 p-3 bg-slate-800 rounded-lg">
              <div className="text-sm text-slate-400">Changing role for:</div>
              <div className="font-semibold">{selectedUser.first_name} {selectedUser.last_name}</div>
              <div className="text-sm text-slate-400">{selectedUser.email}</div>
              {selectedUser.organization_name && (
                <div className="text-sm text-slate-400">Org: {selectedUser.organization_name}</div>
              )}
              {selectedUser.location_name && (
                <div className="text-sm text-slate-400">Location: {selectedUser.location_name}</div>
              )}
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Select New Role
              </label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                className="w-full px-4 py-2 bg-slate-800 border border-slate-700 rounded-lg focus:outline-none focus:border-primary-500"
              >
                <option value="user">User (Employee/Student)</option>
                <option value="location_admin">Location Admin (Office Manager)</option>
                {isSuperAdmin && <option value="org_admin">Org Admin (Business Owner)</option>}
                {isSuperAdmin && <option value="super_admin">Super Admin (Agency Owner)</option>}
              </select>
            </div>

            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 mb-4">
              <div className="text-sm font-semibold mb-2">Role Permissions:</div>
              <ul className="space-y-2 text-sm text-slate-300">
                {newRole === 'user' && (
                  <li>• Regular training access only</li>
                )}
                {newRole === 'location_admin' && (
                  <>
                    <li>• Manage users in their location</li>
                    <li>• Reset passwords for their team</li>
                    <li>• View location-specific reports</li>
                  </>
                )}
                {newRole === 'org_admin' && (
                  <>
                    <li>• Manage ALL locations in organization</li>
                    <li>• Promote users to location admin</li>
                    <li>• Create new locations</li>
                    <li>• View organization-wide reports</li>
                  </>
                )}
                {newRole === 'super_admin' && (
                  <>
                    <li>• Manage ALL organizations and locations</li>
                    <li>• Promote users to any role</li>
                    <li>• View agency-wide reports</li>
                    <li className="text-yellow-400">⚠️ Full system access</li>
                  </>
                )}
              </ul>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowRoleModal(false);
                  setSelectedUser(null);
                  setNewRole('');
                }}
                className="flex-1 btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateRole}
                disabled={!newRole || newRole === selectedUser.role}
                className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Update Role
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-900 border border-primary-500/30 rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Create New User</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">First Name</label>
                <input
                  type="text"
                  value={newUser.firstName}
                  onChange={(e) => setNewUser({ ...newUser, firstName: e.target.value })}
                  className="input"
                  placeholder="John"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Last Name</label>
                <input
                  type="text"
                  value={newUser.lastName}
                  onChange={(e) => setNewUser({ ...newUser, lastName: e.target.value })}
                  className="input"
                  placeholder="Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  className="input"
                  placeholder="john.doe@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Password</label>
                <input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  className="input"
                  placeholder="Min 8 chars, letters, numbers, special chars"
                />
              </div>

              {isSuperAdmin && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium">Organization</label>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateOrgModal(true);
                        setNewOrg({ name: '', tier: 'basic' });
                      }}
                      className="text-xs text-primary-400 hover:text-primary-300"
                    >
                      + Create New
                    </button>
                  </div>
                  <select
                    value={newUser.organizationId}
                    onChange={(e) => setNewUser({ ...newUser, organizationId: e.target.value, locationId: '' })}
                    className="input"
                  >
                    <option value="">Select Organization</option>
                    {displayOrganizations.map(org => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {(isSuperAdmin || isOrgAdmin) && newUser.organizationId && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium">Location (Optional)</label>
                    <button
                      type="button"
                      onClick={() => {
                        setShowCreateLocationModal(true);
                        setNewLocation({ ...newLocation, organizationId: newUser.organizationId });
                      }}
                      className="text-xs text-primary-400 hover:text-primary-300"
                    >
                      + Create New
                    </button>
                  </div>
                  <select
                    value={newUser.locationId}
                    onChange={(e) => setNewUser({ ...newUser, locationId: e.target.value })}
                    className="input"
                  >
                    <option value="">No Location (Org-wide)</option>
                    {locations
                      .filter(loc => loc.organization_id === newUser.organizationId)
                      .map(loc => (
                        <option key={loc.id} value={loc.id}>{loc.name}</option>
                      ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">Role</label>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  className="input"
                >
                  <option value="user">User (Employee/Student)</option>
                  <option value="location_admin">Location Admin (Office Manager)</option>
                  {isSuperAdmin && <option value="org_admin">Org Admin (Business Owner)</option>}
                  {isSuperAdmin && <option value="super_admin">Super Admin (Agency Owner)</option>}
                </select>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewUser({
                    email: '',
                    password: '',
                    firstName: '',
                    lastName: '',
                    role: 'user',
                    organizationId: '',
                    locationId: ''
                  });
                }}
                className="flex-1 btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateUser}
                className="flex-1 btn btn-primary"
              >
                Create User
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Organization Modal */}
      {showCreateOrgModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-slate-900 border border-primary-500/30 rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Create New Organization</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Organization Name *</label>
                <input
                  type="text"
                  value={newOrg.name}
                  onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                  className="input"
                  placeholder="ABC Tax Services"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Subscription Tier</label>
                <select
                  value={newOrg.tier}
                  onChange={(e) => setNewOrg({ ...newOrg, tier: e.target.value })}
                  className="input"
                >
                  <option value="basic">Basic</option>
                  <option value="professional">Professional</option>
                  <option value="enterprise">Enterprise</option>
                </select>
              </div>

              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-sm text-slate-300">
                <p className="font-semibold mb-1">Note:</p>
                <p>A default location will be automatically created for single-office organizations.</p>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateOrgModal(false);
                  setNewOrg({ name: '', tier: 'basic' });
                }}
                className="flex-1 btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateOrganization}
                className="flex-1 btn btn-primary"
              >
                Create Organization
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Location Modal */}
      {showCreateLocationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-slate-900 border border-primary-500/30 rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">Create New Location</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Location Name *</label>
                <input
                  type="text"
                  value={newLocation.name}
                  onChange={(e) => setNewLocation({ ...newLocation, name: e.target.value })}
                  className="input"
                  placeholder="Main Office"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Address</label>
                <input
                  type="text"
                  value={newLocation.address}
                  onChange={(e) => setNewLocation({ ...newLocation, address: e.target.value })}
                  className="input"
                  placeholder="123 Main St"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-2">City</label>
                  <input
                    type="text"
                    value={newLocation.city}
                    onChange={(e) => setNewLocation({ ...newLocation, city: e.target.value })}
                    className="input"
                    placeholder="New York"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">State</label>
                  <input
                    type="text"
                    value={newLocation.state}
                    onChange={(e) => setNewLocation({ ...newLocation, state: e.target.value })}
                    className="input"
                    placeholder="NY"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-2">ZIP Code</label>
                  <input
                    type="text"
                    value={newLocation.zipCode}
                    onChange={(e) => setNewLocation({ ...newLocation, zipCode: e.target.value })}
                    className="input"
                    placeholder="10001"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <input
                    type="text"
                    value={newLocation.phone}
                    onChange={(e) => setNewLocation({ ...newLocation, phone: e.target.value })}
                    className="input"
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateLocationModal(false);
                  setNewLocation({ name: '', organizationId: '', address: '', city: '', state: '', zipCode: '', phone: '' });
                }}
                className="flex-1 btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateLocation}
                className="flex-1 btn btn-primary"
              >
                Create Location
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
